import { Icon, Paragraph } from "@smartsuite/react-ui/lib";
import type { InputGroupState } from "@smartsuite/react-ui/lib/components/forms/InputGroup/InputGroup.constants";
import { GrayColors } from "@smartsuite/types";
import cx from "classnames";
import type React from "react";
import { useRef } from "react";
import { captionToEditorData } from "../../helpers/captionToEditorData";
import type { FormConfigItem } from "../../types/form";
import { $t } from "../../utils/intl";
import { HelpTextTooltip } from "../common/HelpTextTooltip/HelpTextTooltip";
import { SmartDocRenderer } from "../common/SmartDocRenderer/SmartDocRenderer";
import { formFieldControlText } from "./FormFieldControl.text";

export interface FormFieldControlProps {
  label?: string;
  state?: InputGroupState;
  caption?: string;
  captionAsTooltip?: boolean;
  required: boolean;
  readOnly?: boolean;
  htmlFor?: string;
  errorMessage?: string;
  helpText?: FormConfigItem["params"]["helpText"];
  children: React.ReactNode;
}

export const FormFieldControl: React.FC<FormFieldControlProps> = ({
  label,
  state,
  captionAsTooltip,
  caption,
  htmlFor,
  helpText,
  required,
  readOnly,
  errorMessage,
  children,
}) => {
  const labelContainerRef = useRef<HTMLDivElement>(null);
  const helpDoc = helpText?.value ? helpText?.value : captionToEditorData(caption);

  const helpTextEnabled = helpText?.enabled ?? !!caption;

  return (
    <div
      className={cx("form-field-control", {
        "form-field-control--read-only": readOnly,
        [`form-field-control--state-${state}`]: state,
      })}
      aria-label="form-field-control"
    >
      <label htmlFor={htmlFor} className="form-field-control__label">
        <div ref={labelContainerRef} className="form-field-control__label-container">
          {label && (
            <Paragraph weight="medium" size="l-semi-plus" color="currentColor">
              {label}
            </Paragraph>
          )}

          {helpTextEnabled && captionAsTooltip && (
            <HelpTextTooltip
              fieldName={label ?? ""}
              helpText={helpDoc}
              labelContainerRef={labelContainerRef}
            />
          )}
        </div>

        {required && (
          <Paragraph
            className="form-field-control__required"
            color="error"
            size="xxxs"
            weight="semibold"
          >
            {$t(formFieldControlText.required)}
          </Paragraph>
        )}

        {readOnly && (
          <Icon
            className="form-field-control__lock"
            ariaLabel="lock"
            name="lock"
            color={GrayColors["G-4"]}
            size="S"
          />
        )}
      </label>

      {helpTextEnabled && !captionAsTooltip && (
        <SmartDocRenderer editorData={helpDoc} className="form-field-control__caption" />
      )}

      <div className="form-field-control__body">{children}</div>

      {errorMessage && (
        <div className="form-field-control__error-message">
          <Paragraph color="error" size="s">
            {errorMessage}
          </Paragraph>
        </div>
      )}
    </div>
  );
};
